import React, { useState, useMemo, useEffect } from 'react';

const Table = ({
  headers,
  rows,
  className,
  searchable = false,
  sortable = false,
  paginated = false,
  initialPageSize = 10,
  pageSizeOptions = [10, 25, 50, 'all'],
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: 'ascending',
  });
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [page, setPage] = useState(1);

  const isDateString = (value) => !isNaN(Date.parse(value));

  const filteredRows = useMemo(() => {
    if (!searchable || !searchQuery) return rows;
    return rows.filter((row) =>
      row.some((cell) => {
        const cellValue =
          typeof cell === 'string' || typeof cell === 'number'
            ? cell.toString()
            : '';
        return cellValue.toLowerCase().includes(searchQuery.toLowerCase());
      })
    );
  }, [rows, searchQuery, searchable]);

  const sortedRows = useMemo(() => {
    if (!sortable || sortConfig.key === null) return filteredRows;
    const sorted = [...filteredRows].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      if (isDateString(aValue) && isDateString(bValue)) {
        return sortConfig.direction === 'ascending'
          ? new Date(aValue) - new Date(bValue)
          : new Date(bValue) - new Date(aValue);
      }

      const aString =
        typeof aValue === 'string' || typeof aValue === 'number'
          ? aValue.toString()
          : '';
      const bString =
        typeof bValue === 'string' || typeof bValue === 'number'
          ? bValue.toString()
          : '';

      if (aString < bString)
        return sortConfig.direction === 'ascending' ? -1 : 1;
      if (aString > bString)
        return sortConfig.direction === 'ascending' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [filteredRows, sortConfig, sortable]);

  const totalRows = sortedRows.length;
  const isAll = pageSize === 'all';
  const totalPages = isAll ? 1 : Math.max(1, Math.ceil(totalRows / pageSize));

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
    if (page < 1) setPage(1);
  }, [page, totalPages]);

  const pagedRows = useMemo(() => {
    if (!paginated || isAll) return sortedRows;
    const start = (page - 1) * pageSize;
    return sortedRows.slice(start, start + pageSize);
  }, [sortedRows, paginated, isAll, page, pageSize]);

  const handleSort = (index) => {
    if (!sortable) return;
    setSortConfig((prev) => {
      if (prev.key === index) {
        return {
          key: index,
          direction:
            prev.direction === 'ascending' ? 'descending' : 'ascending',
        };
      }
      return { key: index, direction: 'ascending' };
    });
  };

  const startRow = isAll
    ? totalRows
      ? 1
      : 0
    : totalRows
      ? (page - 1) * pageSize + 1
      : 0;
  const endRow = isAll
    ? totalRows
    : Math.min(totalRows, page * (isAll ? totalRows : pageSize));

  return (
    <div className={`${className} overflow-auto rounded-md drop-shadow-md`}>
      {searchable && (
        <input
          type="text"
          placeholder="Search..."
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setPage(1);
          }}
          className="mb-4 p-2 border rounded w-full"
        />
      )}

      <table className="min-w-full bg-foreground">
        <thead>
          <tr className="bg-secondary text-foreground">
            {headers.map((header, index) => (
              <th
                key={index}
                className={`text-left py-3 px-4 font-semibold ${sortable ? 'cursor-pointer' : ''}`}
                onClick={() => handleSort(index)}
              >
                {header}
                {sortable && sortConfig.key === index && (
                  <span>
                    {sortConfig.direction === 'ascending' ? ' 🔼' : ' 🔽'}
                  </span>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {pagedRows.map((row, index) => (
            <tr key={index} className="hover:bg-background">
              {row.map((cell, cellIndex) => (
                <td key={cellIndex} className="py-3 px-4">
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>

      {paginated && (
        <div className="flex justify-between items-center mt-3">
          <div className="flex gap-2">
            <button
              className="btn btn-outline-secondary"
              onClick={() => setPage(1)}
              disabled={page === 1}
            >
              «
            </button>
            <button
              className="btn btn-outline-secondary"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              ‹
            </button>
            <span className="px-2 text-sm">
              {startRow}–{endRow} of {totalRows}
            </span>
            <button
              className="btn btn-outline-secondary"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
            >
              ›
            </button>
            <button
              className="btn btn-outline-secondary"
              onClick={() => setPage(totalPages)}
              disabled={page === totalPages}
            >
              »
            </button>
          </div>

          <div className="flex items-center gap-2">
            <label className="text-sm">Rows per page</label>
            <select
              className="form-select form-select-sm"
              style={{ width: 'auto', display: 'inline-block' }}
              value={pageSize}
              onChange={(e) => {
                const val =
                  e.target.value === 'all'
                    ? 'all'
                    : parseInt(e.target.value, 10);
                setPageSize(val);
                setPage(1);
              }}
            >
              {pageSizeOptions.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}
    </div>
  );
};

export default Table;
